import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UserCheck, Plus, Loader2, RotateCcw, Search } from "lucide-react";

export default function MobiliarioAsignacionesPage() {
  const [asignaciones, setAsignaciones] = useState([]);
  const [activos, setActivos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [ubicaciones, setUbicaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({ activo_id: "", usuario_id: "", ubicacion_id: "", fecha_asignacion: "", observaciones: "" });
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const [asigRes, activosRes, usersRes, ubRes] = await Promise.all([
        axios.get(`${API}/mobiliario/asignaciones?activas=true`, getAuthHeaders()),
        axios.get(`${API}/mobiliario/activos?estado=disponible`, getAuthHeaders()),
        axios.get(`${API}/users`, getAuthHeaders()),
        axios.get(`${API}/mobiliario/ubicaciones`, getAuthHeaders())
      ]);
      setAsignaciones(asigRes.data || []);
      setActivos(activosRes.data || []);
      setUsuarios(usersRes.data || []);
      setUbicaciones(ubRes.data || []);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { 
    setFormData({ activo_id: "", usuario_id: "", ubicacion_id: "", fecha_asignacion: new Date().toISOString().split('T')[0], observaciones: "" }); 
    setShowDialog(true); 
  };

  const handleSubmit = async () => {
    if (!formData.activo_id || !formData.usuario_id) { toast.error("Seleccione activo y usuario"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/mobiliario/asignaciones`, formData, getAuthHeaders());
      toast.success("Activo asignado correctamente");
      setShowDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al asignar"); }
    finally { setSaving(false); }
  };

  const handleDevolver = async (asignacionId) => {
    if (!confirm("¿Confirma la devolución de este activo?")) return;
    try {
      await axios.post(`${API}/mobiliario/asignaciones/${asignacionId}/devolver`, {}, getAuthHeaders());
      toast.success("Activo devuelto"); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900"><Sidebar /><main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></main></div>;

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="mobiliario-asignaciones-page">
        <div className="flex justify-between mb-6">
          <div><h1 className="text-3xl font-bold dark:text-white">Asignaciones de Mobiliario</h1><p className="text-slate-500">{asignaciones.length} asignaciones activas</p></div>
          <Button onClick={openNew} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"><Plus className="w-4 h-4 mr-2" />Nueva Asignación</Button>
        </div>

        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-slate-50 dark:bg-slate-800">
                  <th className="text-left p-4 font-medium text-slate-600">Activo</th>
                  <th className="text-left p-4 font-medium text-slate-600">Usuario</th>
                  <th className="text-left p-4 font-medium text-slate-600">Fecha Asignación</th>
                  <th className="text-left p-4 font-medium text-slate-600">Observaciones</th>
                  <th className="text-right p-4 font-medium text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {asignaciones.length === 0 ? (
                  <tr><td colSpan="5" className="p-8 text-center text-slate-500">No hay asignaciones activas</td></tr>
                ) : asignaciones.map(asig => (
                  <tr key={asig.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="p-4">
                      <p className="font-medium dark:text-white">{asig.activo_nombre}</p>
                      <p className="text-sm text-slate-500">{asig.activo_codigo}</p>
                    </td>
                    <td className="p-4">
                      <p className="font-medium dark:text-white">{asig.usuario_nombre}</p>
                      <p className="text-sm text-slate-500">{asig.usuario_email}</p>
                    </td>
                    <td className="p-4 text-slate-600">{asig.fecha_asignacion}</td>
                    <td className="p-4 text-slate-600 text-sm">{asig.observaciones || "-"}</td>
                    <td className="p-4 text-right">
                      <Button variant="outline" size="sm" onClick={() => handleDevolver(asig.id)}>
                        <RotateCcw className="w-4 h-4 mr-1" />Devolver
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle><UserCheck className="w-5 h-5 inline mr-2" />Nueva Asignación</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Activo a Asignar *</Label>
                <Select value={formData.activo_id} onValueChange={v => setFormData({...formData, activo_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar activo..." /></SelectTrigger>
                  <SelectContent>
                    {activos.map(a => <SelectItem key={a.id} value={a.id}>{a.codigo} - {a.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
                {activos.length === 0 && <p className="text-sm text-amber-600 mt-1">No hay activos disponibles</p>}
              </div>
              <div>
                <Label>Usuario *</Label>
                <Select value={formData.usuario_id} onValueChange={v => setFormData({...formData, usuario_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar usuario..." /></SelectTrigger>
                  <SelectContent>
                    {usuarios.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ubicación</Label>
                <Select value={formData.ubicacion_id} onValueChange={v => setFormData({...formData, ubicacion_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar ubicación..." /></SelectTrigger>
                  <SelectContent>
                    {ubicaciones.map(ub => <SelectItem key={ub.id} value={ub.id}>{ub.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fecha de Asignación</Label>
                <Input type="date" value={formData.fecha_asignacion} onChange={e => setFormData({...formData, fecha_asignacion: e.target.value})} />
              </div>
              <div>
                <Label>Observaciones</Label>
                <Textarea value={formData.observaciones} onChange={e => setFormData({...formData, observaciones: e.target.value})} placeholder="Notas adicionales..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSubmit} disabled={saving} className="bg-[#0C314C] text-white">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Asignar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
